import * as React from "react";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AppSettingsAltRoundedIcon from "@mui/icons-material/AppSettingsAltRounded";
import { grey } from "@mui/material/colors";
//icons
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import PeopleIcon from "@mui/icons-material/People";
import LogoutIcon from "@mui/icons-material/Logout";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import LoginIcon from "@mui/icons-material/Login";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Snackbar from "@mui/material/Snackbar";
import {
  useLocation,
  useNavigate,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import classNames from "classnames";
import { Logo } from "./Logo";
import CircularProgress from "@mui/material/CircularProgress";
import { useProfile } from "../api/hooks";
import SignIn from "../pages/SignIn";
import Users from "../pages/admin/Users";
import Permissions from "../pages/admin/Permissions";
import Businesses from "../pages/admin/Businesses";
import BusinessEdit from "../pages/BusinessEdit";
import UserVerify from "../pages/UserVerify";
import UserEdit from "../pages/UserEdit";
import ContactSales from "../pages/ContactSales";
import ErrorBoundary from "../ErrorBoundary";
import {
  Avatar,
  Box,
  Grow,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { uniqBy } from "lodash";
import Autocomplete from "@mui/material/Autocomplete";
import { useEffect, useMemo } from "react";
import { useMedia } from "react-use";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import Documentation from "../pages/Documentation";

import Checkouts from "../pages/admin/Checkouts";

import AdminBusiness from "../pages/admin/Business";
//import Reports from "../pages/admin/Reports";
import Dashboard from "../pages/admin/Dashboard";
import APIOperators from "../pages/admin/APIOperators";

import FrontPage from "../pages/index/FrontPage";
import CheckoutPage from "../pages/CheckoutPage";
import Terms from "../pages/Terms";
import Privacy from "../pages/Privacy";
import GoogleOauthRedirect from "pages/GoogleOauthRedirect";

const drawerWidth = 240;

function PaperComponent(paperProps, ref) {
  return (
    <Grow in>
      <Paper {...paperProps} elevation={8} ref={ref} />
    </Grow>
  );
}

export function ResponsiveDrawer() {
  const { profile, logout, refetchProfile, isLoading } = useProfile();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("r");
  const isSignedIn = !!profile;
  const isDarkTheme = useMedia("(prefers-color-scheme: dark)");
  const isMobile = useMedia("(max-width: 900px)");
  const theme = useTheme();

  const PaperComponentForward = React.forwardRef(PaperComponent);
  //console.log(profile);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window,
  });

  const userBusinesses: any = useMemo(() => {
    return profile?.roles?.length > 0
      ? uniqBy(
          profile?.roles.map((role: any) => {
            return {
              name: role.business?.name || "",
              id: role.businessId || "",
              type: role.business?.type || "",
              dataSource: role.business?.dataSource || "",
              enabled: role.business?.enabled || false,
            };
          }),
          "id"
        )
      : [];
  }, [profile]);

  const isSuperAdmin =
    profile?.roles?.some((role) => role.type === "SUPER_ADMIN") ?? false;

  const userBusinessOptions = useMemo(() => {
    if (isSuperAdmin) {
      return [...userBusinesses].slice(1);
    } else {
      return [...userBusinesses];
    }
  }, [isSuperAdmin, userBusinesses]);

  const [selectedBusiness, setSelectedBusiness] = React.useState<any>(
    userBusinessOptions.length > 0 ? userBusinessOptions[0] : undefined
  );

  useEffect(() => {
    if (!selectedBusiness) {
      setSelectedBusiness(userBusinessOptions[0]);
    }
  }, [selectedBusiness, userBusinessOptions]);

  const [mobileOpen, setMobileOpen] = React.useState(false);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isAdmin =
    (profile?.roles?.some((role) => role.type === "BUSINESS_ADMIN") ||
      isSuperAdmin) ??
    false;

  const isEventConfigurator =
    (profile?.roles?.some((role) => role.type === "EVENT_CONFIGURATOR") ||
      isAdmin ||
      isSuperAdmin) ??
    false;

  const isEventCreator =
    (profile?.roles?.some((role) => role.type === "EVENT_CREATOR") ||
      isAdmin ||
      isEventConfigurator ||
      isSuperAdmin) ??
    false;

  const isEventReporter =
    (profile?.roles?.some((role) => role.type === "EVENT_REPORTER") ||
      isAdmin ||
      isEventConfigurator ||
      isEventCreator ||
      isSuperAdmin) ??
    false;

  const isTicketInvalidator =
    (profile?.roles?.some((role) => role.type === "TICKET_INVALIDATOR") ||
      isAdmin ||
      isSuperAdmin) ??
    false;

  const isTicketChecker =
    (profile?.roles?.some((role) => role.type === "TICKET_CHECKER") ||
      isAdmin ||
      isSuperAdmin) ??
    false;

  const drawer = (
    <>
      {/* {isMobile && ( */}
      <Toolbar>
        <Box
          onClick={() => {
            handleDrawerToggle();
            navigate("/");
          }}
          className="logo-and-sitename"
        >
          <Logo />
          {/*
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontWeight: '700!important' }}
          >
            {APP_NAME}
          </Typography>
          */}
        </Box>
      </Toolbar>
      {/* )} */}
      <List>
        {/*}
        <div
          className={classNames({
            'list-item-active': location.pathname === '/',
          })}
        >
          
          <ListItem
            button
            onClick={() => {
              navigate('/');
              handleDrawerToggle();
            }}
          >
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            <ListItemText primary={'Events'} />
          </ListItem>
        </div>
          {*/}
        {/*
        <Divider sx={{ margin: '8px 0' }} />
        <Typography
          component="h3"
          sx={{
            // eslint-disable-next-line sonarjs/no-duplicate-string
            margin: '1rem 16px 0.5rem',
            textTransform: 'uppercase',
            fontSize: '0.875rem',
            fontWeight: '500',
            // eslint-disable-next-line sonarjs/no-duplicate-string
            letterSpacing: '0.03125rem',
          }}
        >
          Events
        </Typography>
        */}

        <div
          className={classNames({
            "list-item-active": location.pathname === "/",
          })}
        >
          <ListItem
            button
            onClick={() => {
              navigate("/");
              handleDrawerToggle();
            }}
          >
            <ListItemIcon>
              {isSignedIn ? <QueryStatsOutlinedIcon /> : <HomeOutlinedIcon />}
            </ListItemIcon>
            <ListItemText primary={isSignedIn ? "Dashboard" : "Home"} />
          </ListItem>
        </div>

        {isSuperAdmin && (
          <>
            <div
              className={classNames({
                "list-item-active": location.pathname === "/admin/checkouts",
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate("/admin/checkouts");
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <ShoppingCartCheckoutOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={"Checkouts"} />
              </ListItem>
            </div>
          </>
        )}

        <div
          className={classNames({
            "list-item-active": location.pathname === "/documentation",
          })}
        >
          <ListItem
            button
            onClick={() => {
              navigate("/documentation");
              handleDrawerToggle();
            }}
          >
            <ListItemIcon>
              <DescriptionOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={"Documentation"} />
          </ListItem>
        </div>
        <Divider sx={{ margin: "8px 0" }} />
        {/*}
        {(isTicketChecker || isTicketInvalidator) && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <Typography
              component="h3"
              sx={{
                // eslint-disable-next-line sonarjs/no-duplicate-string
                margin: '1rem 16px 0.5rem',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                fontWeight: '500',
                // eslint-disable-next-line sonarjs/no-duplicate-string
                letterSpacing: '0.03125rem',
              }}
            >
              Tools
            </Typography>
          </>
        )}
        {isTicketInvalidator && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/gate-scanner',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/gate-scanner');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <QrCodeScannerIcon />
              </ListItemIcon>
              <ListItemText primary={'Gate scanner'} />
            </ListItem>
          </div>
        )}
{*/}
        {/* 
        {(isAdmin || null) && (
          <>
            <div
              className={classNames({
                "list-item-active": location.pathname === "/admin/reports",
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate("/admin/reports");
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <SummarizeOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={"Reports"} />
              </ListItem>
            </div>
            <Divider sx={{ margin: "8px 0" }} />
          </>
        )}
 */}
        {(isAdmin ||
          isEventCreator ||
          isEventConfigurator ||
          isEventReporter) && (
          <>
            <Typography
              component="h3"
              sx={{
                margin: "1rem 16px 0.5rem",
                textTransform: "uppercase",
                fontSize: "0.875rem",
                fontWeight: "500",
                letterSpacing: "0.03125rem",
              }}
            >
              Administer
            </Typography>
          </>
        )}

        {isAdmin && (
          <>
            <div
              className={classNames({
                "list-item-active": location.pathname === "/admin/businesses",
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate("/admin/businesses");
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <StorefrontIcon />
                </ListItemIcon>
                <ListItemText primary={"Businesses"} />
              </ListItem>
            </div>
          </>
        )}
        {/* 
        {(isAdmin || null) && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/admin/permissions',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/permissions');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <SupervisorAccountIcon />
              </ListItemIcon>
              <ListItemText primary={'Permissions'} />
            </ListItem>
          </div>
        )}
         */}
        {(isAdmin || null) && (
          <div
            className={classNames({
              "list-item-active": location.pathname === "/admin/operators",
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate(`/admin/operators/`);
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <AppSettingsAltRoundedIcon />
              </ListItemIcon>
              <ListItemText primary={"API operators"} />
            </ListItem>
          </div>
        )}
        {isSuperAdmin && (
          <>
            <div
              className={classNames({
                "list-item-active": location.pathname === "/admin/users",
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate("/admin/users");
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary={"Users"} />
              </ListItem>
            </div>
            <Divider sx={{ margin: "8px 0" }} />
          </>
        )}
        {!isSignedIn && (
          <>
            <div
              className={classNames({
                "list-item-active": location.pathname === "/signin",
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate("/signin");
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText primary={"Sign in"} />
              </ListItem>
            </div>
            <div
              className={classNames({
                "list-item-active": location.pathname === "/signup",
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate("/signup");
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <AccountBoxOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={"Sign up"} />
              </ListItem>
            </div>
            <Divider sx={{ margin: "8px 0" }} />
          </>
        )}
        {isSignedIn && isMobile && (
          <>
            <Box sx={{ maxWidth: "100%" }}>
              <Typography
                component="h3"
                sx={{
                  margin: "1rem 16px 0.5rem",
                  textTransform: "uppercase",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  wordBreak: "break-all",
                  letterSpacing: "0.03125rem",
                  lineHeight: "1.1rem",
                }}
              >
                {profile.username}
              </Typography>
            </Box>
            <div
              className={classNames({
                "list-item-active": location.pathname === "/user-edit",
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate(`/user-edit/${profile.id}`);
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <AccountBoxOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={"Edit account"} />
              </ListItem>
            </div>

            <div>
              <ListItem
                button
                onClick={() => {
                  logout();
                  navigate("/");
                  setSnackbarOpen(true);
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary={"Logout"} />
              </ListItem>
            </div>
            <Divider sx={{ margin: "8px 0" }} />
          </>
        )}
        <Box sx={{ p: "8px 16px 0", opacity: 0.8, fontSize: "0.813rem" }}>
          <Box
            sx={{
              display: "inline",
              mr: { xs: "1.2rem", sm: "0.8rem", cursor: "pointer" },
            }}
            onClick={() => {
              navigate("/terms");
              handleDrawerToggle();
            }}
          >
            Terms
          </Box>
          <Box
            sx={{ display: "inline", cursor: "pointer" }}
            onClick={() => {
              navigate("/privacy");
              handleDrawerToggle();
            }}
          >
            Privacy
          </Box>
          <Box sx={{ mt: "16px" }}>© 2023 Spidipay Ltd.</Box>
        </Box>
      </List>
    </>
  );

  const container =
    window !== undefined ? () => window.document.body : undefined;

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen(false);
  };

  return (
    <Box
      className={isDarkTheme ? "dark-theme" : "light-theme"}
      sx={{ display: "flex" }}
    >
      <CssBaseline />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleClose}
        message="You have been logged out"
      />
      {/*
      ml: { sm: `${drawerWidth}px` }, 
      zIndex: (theme) => isMobile ? theme.zIndex.drawer - 1 : theme.zIndex.drawer + 1, */}

      {window.location.pathname.slice(0, 10) !== "/checkout/" && (
        <AppBar
          className="appbar"
          position="fixed"
          sx={{
            width: { drawerWidth },
            height: "56px",
            color: isDarkTheme
              ? "rgba(255 255 255/0.8)!important"
              : "rgba(32 33 36/0.75)!important",
            background: trigger
              ? !isDarkTheme
                ? "rgba(255 255 255/0.82)"
                : "rgba(32 33 36/0.85)"
              : "transparent!important",
            backdropFilter: trigger ? "blur(10px)" : "none",
            transition: "background 0.25s ease-in-out",
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0, md: "10px" },
              minHeight: { md: "56px" },
            }}
          >
            {/* display: {
            md: 'none',
          }, */}
            <IconButton
              className="menu-btn"
              aria-label="drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: { xs: "-10px", md: 0 }, mr: 2 }} //{ sm: 'none' }
            >
              <MenuIcon />
            </IconButton>
            <Box
              onClick={() => navigate("/")}
              sx={{
                cursor: "pointer",
                width: "auto",
              }}
            >
              <Logo />
              {/*}
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: '700!important' }}
            >
              {APP_NAME}
            </Typography>
          {*/}
            </Box>
            {userBusinessOptions?.length > 0 && (
              <Box
                className="field-with-icon"
                sx={{
                  position: "relative",
                  width: { sm: "300px" },
                  flexGrow: 1,
                  maxWidth: "300px",
                }}
              >
                <Autocomplete
                  PaperComponent={PaperComponentForward}
                  style={{ marginLeft: "12px", marginTop: "1px" }}
                  disablePortal
                  id="combo-box"
                  size="small"
                  color="#fff"
                  options={userBusinessOptions}
                  getOptionLabel={(option: any) => option.name}
                  defaultValue={userBusinessOptions[0]}
                  isOptionEqualToValue={(option: any, value: any) => {
                    return option.id === value.id;
                  }}
                  onChange={(e: any, newValue: any) => {
                    setSelectedBusiness(newValue);
                  }}
                  sx={{
                    width: { sm: "300px" },
                    flexGrow: 1,
                    maxWidth: "300px",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      style={{
                        color: "#ffffff",
                      }}
                    />
                  )}
                />

                <StorefrontIcon
                  className="field-icon"
                  sx={{
                    position: "absolute",
                    top: "8px",
                    left: "22px",
                    opacity: isDarkTheme ? 0.9 : 0.75,
                  }}
                />
              </Box>
            )}
            {isSignedIn && !isMobile && (
              <Box sx={{ flexGrow: 0, marginLeft: "auto", px: "14px" }}>
                <IconButton onClick={handleOpenUserMenu} size="small">
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: grey[600],
                    }}
                  ></Avatar>
                </IconButton>

                <Menu
                  sx={{ mt: "34px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem sx={{ opacity: "1!important" }} disabled>
                    <Typography textAlign="center">
                      {profile.username}
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate(`/user-edit/${profile.id}`);
                      handleCloseUserMenu();
                    }}
                  >
                    <ListItemIcon>
                      <AccountBoxOutlinedIcon />
                    </ListItemIcon>
                    <Typography textAlign="center">Edit account</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      logout();
                      navigate("/");
                      setSnackbarOpen(true);
                      handleCloseUserMenu();
                    }}
                  >
                    <ListItemIcon>
                      <LogoutIcon />
                    </ListItemIcon>
                    <Typography textAlign="center">Logout</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Toolbar>
        </AppBar>
      )}
      {/* sx={{ width: { md: drawerWidth }, flexShrink: { sm: 0 } }} */}
      <Box component="nav" aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        {/* variant= {isMobile ? 'temporary' : 'permanent'} 
         mt: { xs: '0', md: '64px' }, */}
        <Drawer
          className={isDarkTheme ? "dark-theme" : "light-theme"}
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: "block", //display: { xs: 'block', sm: 'none' },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              flex: "0 0 auto",
              flexGrow: 1,
              flexShrink: 0,
              background: isDarkTheme
                ? "#202124!important"
                : "#f2f6fc!important",
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* 
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        */}
      </Box>
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: "0 16px 16px 16px",
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {isLoading && (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}
        <ErrorBoundary fallback={"..."}>
          <React.Suspense
            fallback={
              <Box sx={{ textAlign: "center", mt: 6 }}>
                <CircularProgress />
              </Box>
            }
          >
            {!isLoading && (
              <Routes>
                <Route
                  path="/"
                  element={
                    isSignedIn ? (
                      <Dashboard selectedBusiness={selectedBusiness} />
                    ) : (
                      <FrontPage />
                    )
                  }
                />
                <Route path="/contact/sales" element={<ContactSales />} />
                <Route
                  path="/signin"
                  element={
                    isSignedIn ? <Navigate to={redirect || "/"} /> : <SignIn />
                  }
                />

                {/* user routes */}
                <Route path="/signup" element={<UserEdit />} />

                <Route
                  path="/user-edit/:userId"
                  element={isSignedIn ? <UserEdit /> : <Navigate to="/" />}
                />

                <Route path="/verify/:token" element={<UserVerify />} />

                <Route
                  path="/admin/users"
                  element={
                    isAdmin ? (
                      <Users selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                <Route
                  path="/admin/permissions"
                  element={
                    isAdmin ? (
                      <Permissions selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                {/* businesses routes */}

                <Route
                  path="/admin/businesses"
                  element={
                    isAdmin ? (
                      <Businesses selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                <Route
                  path="/admin/business/:businessId"
                  element={
                    isAdmin ? (
                      <AdminBusiness selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                <Route path="/admin/business-edit">
                  <Route
                    path="/admin/business-edit"
                    //element={isAdmin ? <BusinessEdit /> : <Navigate to="/" />}
                    element={
                      isSignedIn ? <BusinessEdit /> : <Navigate to="/" />
                    }
                  />
                  <Route
                    path="/admin/business-edit/:businessId"
                    element={isAdmin ? <BusinessEdit /> : <Navigate to="/" />}
                  />
                </Route>
                {/* 
                <Route path="/checkout/:sessionId" element={<CheckoutPage />} />
 */}
                <Route
                  path="/admin/checkouts"
                  element={
                    isAdmin ? (
                      <Checkouts selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                {/* 
                <Route
                  path="/admin/reports"
                  element={
                    isAdmin ? (
                      <Reports selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
 */}
                <Route
                  path="/admin/dashboard"
                  element={
                    isAdmin ? (
                      <Dashboard selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route
                  path="/admin/operators"
                  element={
                    isAdmin ? (
                      <APIOperators selectedBusiness={selectedBusiness} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route
                  path="/documentation"
                  element={
                    <Documentation selectedBusiness={selectedBusiness} />
                  }
                />

                <Route
                  path="/auth/google/redirect"
                  element={<GoogleOauthRedirect />}
                />
              </Routes>
            )}
          </React.Suspense>
        </ErrorBoundary>
      </Box>
    </Box>
  );
}
